import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  GridOptionItem,
  POLYGON_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { formatNumberWithMaxDecimal } from "@/helpers/numberHelpers";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  setSelectedAttribute,
  setSpatialAnalysisDatasetId
} from "@/store/slice/analysisSummary";
import { EOutputAnalysisType } from "@/types/spatial";

const getLabelPrefix = (isGrid: boolean, isPolygon: boolean): string => {
  if (isGrid) return "Grid ID";
  if (isPolygon) return "Polygon ID";
  return "Catchment ID";
};

export const useAnalysisSummaryData = () => {
  const dispatch = useDispatch();
  const { isDatasetWidgetEnabled } = useAppFeatures();

  const { datasets, visualizationOrder, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );

  const { gridsSelected, selectedAttribute, generalSummary, isHasSummary } =
    useSelector((state: RootState) => state.analysisSummary);

  const handleAttributeChange = useCallback(
    (option: IOptionItem | null) => {
      dispatch(setSelectedAttribute(option as IOptionItem));
    },
    [dispatch]
  );

  const ATTRIBUTE_OPTIONS = useMemo<IOptionItem[]>(() => {
    const filteredAttribute = generalSummary?.data.attributes.filter(
      (attr) => attr.attribute_name !== "Score"
    );
    const attributeSource = isHasSummary
      ? generalSummary?.data.attributes
      : filteredAttribute;
    return (
      attributeSource?.map((attribute) => ({
        label: attribute.attribute_name,
        value: attribute.attribute_name
      })) || []
    );
  }, [generalSummary?.data.attributes, isHasSummary]);

  const isCatchment = useMemo(
    () =>
      generalSummary?.data.attributes.some((attr) =>
        attr.rows.some((row) => !!row.catchment_id)
      ) || false,
    [generalSummary?.data.attributes]
  );

  const isPolygon = useMemo(
    () =>
      generalSummary?.data.attributes.some((attr) =>
        attr.rows.some((row) => !!row.polygon_id)
      ) || false,
    [generalSummary?.data.attributes]
  );

  const GRID_DATA = useMemo(
    () =>
      generalSummary?.data.attributes
        .find((attr) => attr.attribute_name === selectedAttribute?.value)
        ?.rows.map((row) => ({
          name: row.catchment_id || row.polygon_id || row.grid_id || "",
          value: formatNumberWithMaxDecimal(row.value, 3)
        })) || [],
    [generalSummary?.data.attributes, selectedAttribute?.value]
  );

  // Prepare list of ordered and original datasets
  const orderedDatasets = useMemo(
    () => visualizationOrder.map((id) => datasets[id]),
    [visualizationOrder, datasets]
  );
  const spatialAnalysisDatasetId = useMemo(
    () =>
      orderedDatasets.find(
        (dataset) =>
          dataset?.data?.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME
      )?.data?.id ?? "",
    [orderedDatasets]
  );
  const analysisResultDataset = rawDatasets[spatialAnalysisDatasetId];

  const originalDatasets = useMemo(
    () =>
      Object.entries(rawDatasets)
        .filter(([id]) => id !== spatialAnalysisDatasetId)
        .map(([, dataset]) => dataset),
    [rawDatasets, spatialAnalysisDatasetId]
  );

  const gridValue: GridOptionItem[] = useMemo(() => {
    if (!analysisResultDataset) return [];

    const { columns, rows, dataset_id } = analysisResultDataset;

    // Find column indices
    const gridIndex = columns.findIndex(
      (col) => col.name === GRID_ID_ATTRIBUTE
    );
    const catchmentIndex = columns.findIndex(
      (col) => col.name === CATCHMENT_ID_ATTRIBUTE
    );
    const polygonIndex = columns.findIndex(
      (col) => col.name === POLYGON_ID_ATTRIBUTE
    );
    const scoreIndex = columns.findIndex((col) => col.name === "Score");

    if (gridIndex === -1 && catchmentIndex === -1 && polygonIndex === -1)
      return [];

    // Determine which index and label to use
    const isGrid = gridIndex !== -1;
    const hasPolygonIndex = polygonIndex !== -1;
    const labelPrefix = getLabelPrefix(isGrid, hasPolygonIndex);

    let idIndex = catchmentIndex;

    if (isGrid) idIndex = gridIndex;
    else if (hasPolygonIndex) idIndex = polygonIndex;

    return rows
      .map((row) => {
        const idValue = row[idIndex] ?? "";
        const typeSafeIdValue =
          typeof idValue === "string" ? idValue : String(idValue);

        const scoreValue = row[scoreIndex] ?? 0;
        const typeSafeScoreValue =
          typeof scoreValue === "number" ? scoreValue : 0;

        return {
          datasetId: dataset_id,
          label: `${labelPrefix}: ${typeSafeIdValue}`,
          id: typeSafeIdValue,
          value: typeSafeIdValue,
          score: typeSafeScoreValue,
          suffix: typeSafeScoreValue.toLocaleString()
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [analysisResultDataset]);

  useEffect(() => {
    dispatch(setSpatialAnalysisDatasetId(spatialAnalysisDatasetId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spatialAnalysisDatasetId]);

  const isAllSelected = gridsSelected?.[0] === DROPDOWN_ALL_VALUE;

  // Registry for per-dataset category normalizers. Each DatasetSection registers
  // its setAnalysisConfiguration-based callback; FilterByAnalysisArea invokes them
  // from handleSelectGrid when grids change to ALL.
  const categoryNormalizersRef = useRef<Map<string, () => void>>(new Map());

  const registerCategoryNormalizer = useCallback(
    (datasetId: string, normalize: () => void) => {
      categoryNormalizersRef.current.set(datasetId, normalize);
      return () => {
        categoryNormalizersRef.current.delete(datasetId);
      };
    },
    []
  );

  const runCategoryNormalizers = useCallback(() => {
    categoryNormalizersRef.current.forEach((normalize) => normalize());
  }, []);

  // Track which DatasetSections have finished loading their summary so the PDF
  // download menu can be gated until every chart has real content (otherwise
  // html2canvas snapshots LoadingWrapper spinners).
  const [readyDatasetIds, setReadyDatasetIds] = useState<Set<string>>(
    new Set()
  );

  const reportDatasetReady = useCallback(
    (datasetId: string, isReady: boolean) => {
      setReadyDatasetIds((prev) => {
        if (prev.has(datasetId) === isReady) return prev;
        const next = new Set(prev);
        if (isReady) {
          next.add(datasetId);
        } else {
          next.delete(datasetId);
        }
        return next;
      });
    },
    []
  );

  // In widget mode DatasetSection is never mounted so reportDatasetReady is
  // never called — treat PDF as always ready so the button isn't permanently disabled.
  const isAllDatasetsReady =
    isDatasetWidgetEnabled ||
    (originalDatasets.length > 0 &&
      originalDatasets.every((dataset) =>
        readyDatasetIds.has(dataset.dataset_id)
      ));

  const isSiteProfiling =
    analysisResultDataset?.spatial_aggregation_type ===
    EOutputAnalysisType.PROFILING;

  const getTypeSummary = useCallback(() => {
    if (isCatchment) return "Catchment";
    if (isPolygon) return "Polygon";
    return "Grid";
  }, [isCatchment, isPolygon]);

  return useMemo(
    () => ({
      ATTRIBUTE_OPTIONS,
      isCatchment,
      isPolygon,
      GRID_DATA,
      gridValue,
      spatialAnalysisDatasetId,
      handleAttributeChange,
      isAllSelected,
      isSiteProfiling,
      originalDatasets,
      getTypeSummary,
      registerCategoryNormalizer,
      runCategoryNormalizers,
      reportDatasetReady,
      isAllDatasetsReady
    }),
    [
      ATTRIBUTE_OPTIONS,
      isCatchment,
      isPolygon,
      GRID_DATA,
      gridValue,
      spatialAnalysisDatasetId,
      handleAttributeChange,
      isAllSelected,
      isSiteProfiling,
      originalDatasets,
      getTypeSummary,
      registerCategoryNormalizer,
      runCategoryNormalizers,
      reportDatasetReady,
      isAllDatasetsReady
    ]
  );
};
